import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SEO_LANDING_PAGES } from "../data/seoLandingPages";

const SITE = "https://therankersacademy.in";
const OG_IMAGE = `${SITE}/assets/og/og-default.jpg`;
const BRAND = "The Rankers Academy";

// Per-route title + description for the public marketing pages.
const PAGES = {
  "/": {
    title: `${BRAND} | JEE Advanced, NEET-UG & MHT-CET Coaching in Nagpur`,
    desc: "Premier JEE, NEET & MHT-CET coaching in Laxmi Nagar, Nagpur, founded by VNIT Nagpur alumni. Daily micro-tests, direct founder mentorship and proven ranker results.",
  },
  "/admissions": {
    title: `Admissions 2027-29 | ${BRAND}`,
    desc: "Admissions open for the 2027-29 batches at The Rankers Academy, Nagpur. JEE, NEET and MHT-CET programmes. Early-bird offers — enquire today.",
  },
  "/rtse": {
    title: `RTSE 3.0 Scholarship Exam — up to 90% Off | ${BRAND}`,
    desc: "Rankers Talent Search cum Scholarship Examination (RTSE 3.0). Win up to 90% scholarship for classes 8, 9 & 10. Register for the next slot in Nagpur.",
  },
  "/courses": {
    title: `Courses — JEE, NEET & MHT-CET Programmes | ${BRAND}`,
    desc: "Two-year and foundation programmes for JEE Advanced, NEET-UG and MHT-CET at The Rankers Academy, Nagpur. Taught directly by VNIT alumni directors.",
  },
  "/faculty": {
    title: `Faculty — VNIT Nagpur Alumni Mentors | ${BRAND}`,
    desc: "Meet the teaching bench at The Rankers Academy — VNIT Nagpur alumni with 12+ years of pedagogy. No surrogate junior faculty; directors teach you directly.",
  },
  "/methodology": {
    title: `Our Method — Daily Micro-Tests & DPPs | ${BRAND}`,
    desc: "The Rankers method: daily tab micro-tests, dedicated DPPs and direct founder mentorship that builds rank, brick by brick.",
  },
  "/toppers": {
    title: `Our Results & Toppers | ${BRAND}`,
    desc: "Verified results from our regular classroom batches in Laxmi Nagar, Nagpur. See the ranks our students have earned.",
  },
  "/campus": {
    title: `Campus — Laxmi Nagar, Nagpur | ${BRAND}`,
    desc: "Our Nagpur campus at Plot 10, Buty Layout, RPTS Road, Laxmi Nagar — 200m from Mate Square. Focused, distraction-free learning environment.",
  },
  "/residential": {
    title: `Residential Programme | ${BRAND}`,
    desc: "Residential coaching option at The Rankers Academy, Nagpur — structured study, mentorship and a distraction-free routine for serious aspirants.",
  },
  "/downloads": {
    title: `Downloads — Prospectus & Study Material | ${BRAND}`,
    desc: "Download the prospectus, study material and free resources from The Rankers Academy, Nagpur.",
  },
  "/workshop": {
    title: `Free Workshop | ${BRAND}`,
    desc: "Join the free Sunday workshop (offline, 10 AM-12 PM) at The Rankers Academy, Nagpur. Limited seats.",
  },
  "/demo": {
    title: `Book a Demo Class | ${BRAND}`,
    desc: "Book a free demo class at The Rankers Academy, Nagpur and experience our teaching before you enrol.",
  },
  "/privacy-policy": {
    title: `Privacy Policy | ${BRAND}`,
    desc: "How The Rankers Academy collects, uses, shares and protects your personal data, your rights, and how to request account and data deletion.",
  },
};

// Merge the SEO landing pages (course+city and Vidarbha district pages) so each gets a unique title/description.
SEO_LANDING_PAGES.forEach((p) => {
  PAGES[p.path] = { title: p.title, desc: p.metaDescription };
});

// Routes that must never be indexed (private product surfaces).
const NOINDEX_PREFIXES = ["/portal", "/admin", "/control", "/dmt", "/faculty-desk", "/live-board", "/parent", "/login", "/signup", "/change-password", "/my-progress", "/verify", "/rtse/login", "/rtse/counsellor", "/rtse/result", "/rtse/certificate"];

export default function Seo() {
  const { pathname } = useLocation();
  const p = pathname.replace(/\/+$/, "") || "/";
  const noindex = NOINDEX_PREFIXES.some((pre) => p === pre || p.startsWith(pre + "/") || p.startsWith(pre));
  const meta = PAGES[p] || PAGES["/"];
  const canonical = SITE + (p === "/" ? "/" : p);

  return (
    <Helmet prioritizeSeoTags>
      <title>{meta.title}</title>
      <meta name="description" content={meta.desc} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}
      {!noindex && <link rel="canonical" href={canonical} />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.desc} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
}
